import React from 'react';
import {useNavigate, useParams} from 'react-router';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import {Box, Chip, Divider, Stack} from "@mui/material";
import useFeedItems from "./useFeedItems";
import FeedItemStack from "./FeedItemStack";
import FeedHeader from "./FeedHeader";
import {useSession} from "../../../../firebase/UserProvider";
import {formatDate} from "../../../../utils/formatTimestamp";

const FeedItems = ({onFeedUpdated, refreshKey = 0}) => {
    const {feedId, sourceSegmentId} = useParams();
    const navigate = useNavigate();
    const {user} = useSession() || {};
    const {items, loading, error, markRead, markUnread, markHitRead, markHitUnread} = useFeedItems(feedId, user?.uid, refreshKey);

    // Show placeholder when there is no feedId
    if (!feedId) {
        return (
            <Box p={2}>
                <Typography variant="h6" gutterBottom>No feed selected</Typography>
                <Typography variant="body2">Please select a feed from the feeds list to view its items.</Typography>
            </Box>
        );
    }

    if (loading) return <div style={{padding: 16}}><CircularProgress size={24}/> <span style={{marginLeft: 8}}>Loading items...</span>
    </div>;
    if (error) return <div style={{padding: 16}}>Error loading items: {String(error.message || error)}</div>;

    const onClickItem = (item) => {
        // navigate to readbook item page by id (adjust route as needed)
        navigate(item.reportId);
    };

    // Returns a stable ISO date key (YYYY-MM-DD) for grouping
    const dateKey = (published) => {
        if (!published) return 'unknown';
        try {
            const d = new Date(published);
            if (isNaN(d.getTime())) return String(published).slice(0, 10);
            return d.toISOString().split('T')[0];
        } catch (e) {
            return String(published).slice(0, 10);
        }
    };

    // Human-readable label for a group header (e.g. "Today", "Yesterday", "May 20, 2026")
    const formatGroupLabel = (isoDateKey) => {
        if (isoDateKey === 'unknown') return 'Unknown date';
        try {
            const d = new Date(isoDateKey + 'T00:00:00');
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            const todayKey = today.toISOString().split('T')[0];
            const yestKey = yesterday.toISOString().split('T')[0];
            if (isoDateKey === todayKey) return 'Today';
            if (isoDateKey === yestKey) return 'Yesterday';
            return formatDate(d, isoDateKey);
        } catch (e) {
            return isoDateKey;
        }
    };

    const groups = items.reduce((acc, it) => {
        const key = dateKey(it.published);
        if (!acc[key]) acc[key] = [];
        acc[key].push(it);
        return acc;
    }, {});

    const unreadCount = items.filter(it => it.read === false).length;

    return (
        <Stack>
            <FeedHeader feedId={feedId} unreadCount={unreadCount} onFeedUpdated={onFeedUpdated}/>

            {items.length === 0 ? (
                <Typography variant="body2" color="text.secondary" p={2}>No items found</Typography>
            ) : (
                Object.keys(groups)
                    .sort((a, b) => (a < b ? 1 : -1))
                    .map((key) => (
                        <Box key={key}>
                            {/* Timeline date separator */}
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                px={2}
                                py={1.5}
                                sx={{position: 'sticky', top: 0, bgcolor: '#fafafa', zIndex: 1, borderBottom: '1px solid #eeeeee'}}
                            >
                                <Chip
                                    label={formatGroupLabel(key)}
                                    size="small"
                                    variant="outlined"
                                    sx={{fontWeight: 600, fontSize: '0.75rem', borderRadius: '6px'}}
                                />
                            </Stack>

                            <Stack>
                                {groups[key].map(item => (
                                    <FeedItemStack
                                        key={item.reportId || item.openTarget?.segmentId || item.openTarget?.referenceId || item.id || JSON.stringify(item.openTarget)}
                                        item={item}
                                        onClick={() => onClickItem(item)}
                                        activeItemId={sourceSegmentId}
                                        onMarkRead={markRead}
                                        onMarkUnread={markUnread}
                                        onMarkHitRead={markHitRead}
                                        onMarkHitUnread={markHitUnread}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    ))
            )}
        </Stack>
    );
};

export default FeedItems;